'use client';

import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { ACTIVITY_SEARCH_MODE_QUERY_PARAMS, ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS } from './shared';
import { useActivitySearchModeStore } from './activity-search-mode-store';
import { useAiSearchEnabled } from './useActivitySearchMode';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from './ai-search-analytics';
import { useActivityAiChat } from './useActivityAiChat';
import { mapAiFiltersToFilterState, pickCarryoverFilters } from './activity-ai-mode-carryover';
import { useActivitySearchCtx } from './client/context';

/**
 * Keeps `?mode=` (nuqs) and `useActivitySearchModeStore` in sync for activity search pages,
 * and — on the `ai` → `normal` transition — runs the AI → Filter carryover (FUN-4891): a pure,
 * discrete mapper fired once at toggle time, not an ambient bridge. Mount once inside
 * `ActivitySearchCtxProvider`.
 */
export function ActivitySearchModeBridge() {
  const [mode, setModeUrl] = useQueryState('mode', ACTIVITY_SEARCH_MODE_QUERY_PARAMS.mode.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS));
  const [chatIdParam] = useQueryState('chat', parseAsString.withDefault(''));

  // AI Search is disabled on some portals (BIZZ). There we derive the mode as
  // `normal` regardless of `?mode=ai` / `?chat=` deep links, so the AI surface
  // can never open even via a shared URL. A stale `?mode=ai` in the URL is inert
  // — nothing reads it once `resolved` overrides — so no cleanup effect needed.
  const aiEnabled = useAiSearchEnabled();
  const resolved = aiEnabled ? mode ?? 'normal' : 'normal';
  const analytics = useAiSearchAnalytics();
  const previousModeRef = useRef<typeof resolved | null>(null);
  useEffect(() => {
    const previous = previousModeRef.current;
    previousModeRef.current = resolved;
    if (previous === null) return; // first mount: establish baseline, no event
    if (previous === resolved) return;
    analytics[AI_SEARCH_EVENTS.MODE_SWITCHED]({
      to_mode: resolved,
      from_mode: previous
    });
  }, [resolved, analytics]);

  // AI → Filter carryover (FUN-4891): read straight off the active chat's
  // messages so `setMode` below can map the AI's last applied filters onto
  // the filter-drawer state at the exact moment the user leaves AI mode —
  // a discrete toggle-time transform, not an ambient per-turn URL push.
  const {
    messages: aiMessages
  } = useActivityAiChat(chatIdParam || null);
  const filters = useActivitySearchCtx().useFilters();
  useLayoutEffect(() => {
    useActivitySearchModeStore.setState({
      mode: resolved,
      setMode: next => {
        // AI → Filter: pure mapper, fired once on the ai → normal transition.
        // Semantic-only fields (expandedQuery/themes/vibe/etc.) never entered
        // the mapping, so they drop for free.
        if (resolved === 'ai' && next === 'normal') {
          const carryoverFilters = pickCarryoverFilters(aiMessages);
          if (carryoverFilters) void filters.update(mapAiFiltersToFilterState(carryoverFilters));
        }
        void setModeUrl(next === 'normal' ? null : next);
      }
    });
  }, [resolved, setModeUrl, aiMessages, filters]);
  const didPromoteChatOnMountRef = useRef(false);
  useEffect(() => {
    // Initial landing with `?chat=<id>` but no `?mode=ai` (deep link / refresh):
    // promote mode once so the chat surface opens. Skipped on subsequent renders
    // so the user can still toggle back to Filters without being forced into AI.
    // Never promote on a portal where AI is disabled.
    if (didPromoteChatOnMountRef.current) return;
    didPromoteChatOnMountRef.current = true;
    if (aiEnabled && chatIdParam.length > 0 && resolved !== 'ai') {
      void setModeUrl('ai');
    }
  }, [aiEnabled, resolved, chatIdParam, setModeUrl]);
  useEffect(() => () => {
    useActivitySearchModeStore.setState({
      mode: 'normal',
      setMode: () => {
        /* no-op after unmount */
      }
    });
  }, []);
  return null;
}